import { useEffect, useState } from 'react'
import type { FilterObject } from 'app/filter.utils'
import { useFeatureToggleState } from 'app/hooks/useFeatureToggle'
import { useCustomPageStore, useListStore } from 'app/stores/StoreContext'
import type { CustomPageJson, CustomPageType, CustomPages } from 'app/types/entities'
import type { SavedSubpage } from 'app/types/saved-subpage'
import {
  acknowledgePendingPageId,
  consumeLastCreatedPage,
  isDeletedPageId,
  isPendingPageId,
  useCustomPageCreated,
  useCustomPageDeleted,
} from 'lib/customPageEvents'
import { logError } from 'lib/logging'
import { savedPagesOf } from 'lib/savedLists'

const toSubpage = (page: CustomPageType): SavedSubpage => ({
  id: page.id!,
  name: page.name ?? ``,
  filters: (typeof page.filters === `string` ? JSON.parse(page.filters) : (page.filters ?? [])) as FilterObject[],
  join: page.join ?? `and`,
})

/**
 * A record page's saved lists: from Lists under the grouped navigation, else from the saved pages.
 * Nothing is read until the toggle has answered, so a page never reads the wrong source. A list
 * just made or deleted shows at once, before the read catches up. Call from an `observer()` component.
 */
export function useSavedSubpages(page: CustomPages): { subpages: SavedSubpage[]; loaded: boolean } {
  const { enabled: fromLists, settled } = useFeatureToggleState(`grouped-navigation`)
  const customPageStore = useCustomPageStore()
  const listStore = useListStore()
  const [subpages, setSubpages] = useState<SavedSubpage[]>([])
  const [loaded, setLoaded] = useState(false)

  const read = async (): Promise<CustomPageJson[]> => {
    if (fromLists) {
      await listStore.fetchList()
      return savedPagesOf(listStore.hits, page)
    }
    const result = await customPageStore.fetchList({ search: `type:${page}` })
    if (result.error) throw result.error
    return ((result.data as { customPages?: { hits?: CustomPageJson[] } })?.customPages?.hits ?? []).filter(
      (hit) => !isDeletedPageId(hit.id!),
    )
  }

  useEffect(() => {
    if (!settled) return
    read()
      .then((hits) => {
        for (const hit of hits) if (hit.id) acknowledgePendingPageId(hit.id)
        setSubpages((prev) => {
          const hitIds = new Set(hits.map((hit) => hit.id))
          const optimistic = prev.filter((subpage) => !hitIds.has(subpage.id) && isPendingPageId(subpage.id))
          return [...hits.map(toSubpage), ...optimistic]
        })
        setLoaded(true)
      })
      .catch((err) => {
        logError(`Error fetching custom pages`, err)
        setLoaded(true)
      })
  }, [settled, fromLists])

  const insert = (created: CustomPageType) =>
    setSubpages((prev) => (prev.some((subpage) => subpage.id === created.id) ? prev : [...prev, toSubpage(created)]))

  // A list created just before navigating here.
  useEffect(() => {
    const pending = consumeLastCreatedPage()
    if (pending && pending.page === page && pending.id && pending.name) insert(pending)
  }, [])

  useCustomPageDeleted((id) => setSubpages((prev) => prev.filter((subpage) => subpage.id !== id)))
  useCustomPageCreated((created) => {
    if (created.page === page && created.id && created.name) insert(created)
  })

  return { subpages, loaded }
}
